import type { DragEvent as ReactDragEvent, FormEvent, MouseEvent as ReactMouseEvent } from "react";
import { useId, useRef, useState } from "react";
import { useUploadProvisionCsv } from "../hooks";
import type { ProvisionJobSummary } from "../types";
import { useAuth } from "../../auth/useAuth";
import { apiClient } from "../../../lib/apiClient";
import { provisionTemplateForRole } from "../../../lib/provisionTemplate";
import "../../../App.css";

interface UploadCsvButtonProps {
  onUploaded?: (job: ProvisionJobSummary) => void;
  /** Label for the trigger that opens the upload modal */
  triggerLabel?: string;
  /** Primary matches dashboard “New upload”; outline keeps a softer button */
  triggerVariant?: "primary" | "outline";
}

const UploadCsvButton = ({
  onUploaded,
  triggerLabel = "New upload",
  triggerVariant = "primary"
}: UploadCsvButtonProps) => {
  const { user } = useAuth();
  const dropzoneGraphicId = useId().replace(/:/g, "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadMutation = useUploadProvisionCsv();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templateDownloadError, setTemplateDownloadError] = useState<string | null>(null);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  /** Request body finished uploading; server still publishing rows to SQS. */
  const [serverProcessing, setServerProcessing] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedFile(null);
    setError(null);
    setServerProcessing(false);
    setUploadPercent(null);
    setTemplateDownloadError(null);
    setTemplateDownloading(false);
    setDragDepth(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const assignCsvFile = (file: File | null) => {
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Please choose a file with a .csv extension.");
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const downloadTemplate = async () => {
    setTemplateDownloadError(null);
    setTemplateDownloading(true);
    try {
      const { apiPath, filename } = provisionTemplateForRole(user?.role);
      const res = await apiClient.get<Blob>(apiPath, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(res.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setTemplateDownloadError("Unable to download the template. Confirm you’re signed in and try again.");
    } finally {
      setTemplateDownloading(false);
    }
  };

  const handleDropzoneDragEnter = (event: ReactDragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragDepth((d) => d + 1);
  };

  const handleDropzoneDragLeave = (event: ReactDragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragDepth((d) => Math.max(0, d - 1));
  };

  const handleDropzoneDragOver = (event: ReactDragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDropzoneDrop = (event: ReactDragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragDepth(0);
    assignCsvFile(event.dataTransfer.files?.[0] ?? null);
  };

  const closeModal = () => {
    setIsOpen(false);
    resetForm();
    uploadMutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedFile) {
      setError("Select a CSV file to create a job.");
      return;
    }
    if (!name.trim()) {
      setError("Enter a job name before submitting.");
      return;
    }

    setError(null);
    setServerProcessing(false);
    setUploadPercent(0);

    try {
      const result = await uploadMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        file: selectedFile,
        onUploadProgress: ({ loaded, total, percent }) => {
          setUploadPercent(percent);
          if (total > 0 && loaded >= total) {
            setServerProcessing(true);
          }
          if (percent !== null && percent >= 99) {
            setServerProcessing(true);
          }
        }
      });
      if (result?.job) {
        onUploaded?.(result.job);
      }
      // 202 from the API means the rows are queued; the worker processes them
      // out-of-band. Close the modal immediately — operators track progress on
      // the jobs list and retrieve any failed credentials via per-job bulk-reset.
      closeModal();
    } catch (submitError) {
      console.error("Failed to create provisioning job", submitError);
      const maybeAxios = submitError as {
        response?: { data?: unknown; status?: number };
        message?: string;
      };
      const data = maybeAxios?.response?.data as { detail?: unknown } | undefined;
      const detail =
        typeof data?.detail === "string"
          ? data.detail
          : maybeAxios?.message || "Failed to create job. Try again.";
      setError(detail);
    } finally {
      setServerProcessing(false);
      setUploadPercent(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        disabled={uploadMutation.isPending}
        className={triggerVariant === "primary" ? "dashboard-btn-primary" : undefined}
        style={
          triggerVariant === "primary"
            ? undefined
            : {
                padding: "0.5rem 1rem",
                borderRadius: "0.75rem",
                border: "1px solid #2563eb",
                backgroundColor: "#dbeafe",
                color: "#1d4ed8",
                fontWeight: 600,
                cursor: uploadMutation.isPending ? "not-allowed" : "pointer"
              }
        }
      >
        {triggerVariant === "primary" ? <span aria-hidden>+</span> : null}
        {triggerLabel}
      </button>

      {isOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-job-modal-title"
          onClick={(event: ReactMouseEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget && !uploadMutation.isPending) {
              closeModal();
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "1rem",
              width: "100%",
              maxWidth: "520px",
              boxShadow: "0 30px 60px rgba(15, 23, 42, 0.25)",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "1rem",
                padding: "1.25rem 1.5rem",
                borderBottom: "1px solid #e2e8f0"
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h2 id="upload-job-modal-title" style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em" }}>
                  Create provisioning job
                </h2>
                <p className="upload-job-modal-subtitle">Upload a CSV to start a new provisioning batch.</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={uploadMutation.isPending}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  lineHeight: 1,
                  cursor: uploadMutation.isPending ? "not-allowed" : "pointer",
                  color: "#94a3b8",
                  flexShrink: 0
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              aria-busy={uploadMutation.isPending}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                padding: "1.5rem"
              }}
            >
              {(
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <label htmlFor="job-name" style={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}>
                      Job name
                    </label>
                    <input
                      id="job-name"
                      type="text"
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value);
                        if (error) {
                          setError(null);
                        }
                      }}
                      placeholder="e.g. Q1 onboarding batch"
                      required
                      style={{
                        padding: "0.6rem 0.75rem",
                        borderRadius: "0.5rem",
                        border: "1px solid #d0d7e2"
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <label htmlFor="job-description" style={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}>
                      Description <span style={{ fontWeight: 500, color: "#94a3b8" }}>(optional)</span>
                    </label>
                    <textarea
                      id="job-description"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      rows={3}
                      placeholder="Add context for reviewers or auditors"
                      style={{
                        padding: "0.6rem 0.75rem",
                        borderRadius: "0.5rem",
                        border: "1px solid #d0d7e2",
                        resize: "vertical"
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <span id="upload-job-file-label" style={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}>
                      Provisioning file
                    </span>
                    <label
                      htmlFor="job-file"
                      className={`upload-job-dropzone ${dragDepth > 0 ? "upload-job-dropzone--active" : ""}`}
                      onDragEnter={handleDropzoneDragEnter}
                      onDragLeave={handleDropzoneDragLeave}
                      onDragOver={handleDropzoneDragOver}
                      onDrop={handleDropzoneDrop}
                    >
                      <input
                        id="job-file"
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.text/csv"
                        className="visually-hidden"
                        disabled={uploadMutation.isPending}
                        aria-labelledby="upload-job-file-label"
                        onChange={(event) => assignCsvFile(event.target.files?.[0] ?? null)}
                      />
                      {selectedFile ? (
                        <>
                          <span className="upload-job-dropzone-file">{selectedFile.name}</span>
                          <span className="upload-job-dropzone-muted">Click to replace · CSV only</span>
                        </>
                      ) : (
                        <>
                          <div className="upload-job-dropzone-graphic" aria-hidden>
                            <svg
                              width="52"
                              height="58"
                              viewBox="0 0 52 58"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="upload-job-dropzone-svg"
                            >
                              <defs>
                                <linearGradient
                                  id={`${dropzoneGraphicId}-paper`}
                                  x1="6"
                                  y1="4"
                                  x2="6"
                                  y2="52"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#ffffff" />
                                  <stop offset="1" stopColor="#f1f5f9" />
                                </linearGradient>
                                <linearGradient
                                  id={`${dropzoneGraphicId}-fold`}
                                  x1="34"
                                  y1="4"
                                  x2="46"
                                  y2="16"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#dbeafe" />
                                  <stop offset="1" stopColor="#bfdbfe" />
                                </linearGradient>
                              </defs>
                              <rect
                                x="5"
                                y="3"
                                width="38"
                                height="48"
                                rx="5"
                                fill={`url(#${dropzoneGraphicId}-paper)`}
                                stroke="#cbd5e1"
                                strokeWidth="1.2"
                              />
                              <path
                                d="M33 3h10L33 13Z"
                                fill={`url(#${dropzoneGraphicId}-fold)`}
                                stroke="#93c5fd"
                                strokeWidth="0.9"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M33 13L43 3"
                                fill="none"
                                stroke="#7dd3fc"
                                strokeWidth="0.9"
                                strokeLinecap="round"
                                opacity="0.75"
                              />
                              <rect x="12" y="14" width="24" height="2.2" rx="1.1" fill="#e2e8f0" />
                              <rect x="12" y="20" width="18" height="2.2" rx="1.1" fill="#e2e8f0" />
                              <rect x="12" y="26" width="20" height="2.2" rx="1.1" fill="#e2e8f0" />
                              <rect x="10" y="36" width="28" height="12" rx="3" fill="#2563eb" />
                              <text
                                x="24"
                                y="45"
                                textAnchor="middle"
                                fill="#ffffff"
                                fontSize="7"
                                fontWeight="700"
                                fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
                              >
                                CSV
                              </text>
                            </svg>
                          </div>
                          <span className="upload-job-dropzone-muted">
                            Drop your CSV here or{" "}
                            <button
                              type="button"
                              className="upload-job-browse-link"
                              disabled={uploadMutation.isPending}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                fileInputRef.current?.click();
                              }}
                            >
                              browse files
                            </button>
                          </span>
                        </>
                      )}
                    </label>
                    <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>CSV files only</span>
                  </div>

                  <div className="upload-job-callout">
                    <div style={{ flexShrink: 0, marginTop: "0.1rem" }} aria-hidden>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="1.75" />
                        <path
                          d="M12 16v-4M12 8h.01"
                          stroke="#2563eb"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p className="upload-job-callout-title">Before uploading:</p>
                      <ul>
                        {[
                          "Use the template from this page.",
                          "Match the super-admin template headers exactly — do not rename or reorder columns.",
                          "If names include accents or other special characters, save the file as CSV UTF-8 (Excel: Save As → CSV UTF-8 (comma delimited) (*.csv)).",
                        ].map((text) => (
                          <li key={text}>{text}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div>
                    <div className="upload-job-template-row">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#64748b"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" />
                      </svg>
                      <span>Need a template?</span>
                      <button
                        type="button"
                        className="upload-job-template-btn"
                        disabled={uploadMutation.isPending || templateDownloading}
                        onClick={() => void downloadTemplate()}
                      >
                        Download template
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                    {templateDownloadError ? (
                      <div style={{ color: "#b91c1c", fontSize: "0.82rem", marginTop: "0.35rem" }}>
                        {templateDownloadError}
                      </div>
                    ) : null}
                  </div>

                  {error ? <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{error}</div> : null}

                  {uploadMutation.isPending ? (
                    <div
                      role="status"
                      aria-live="polite"
                      style={{
                        padding: "0.85rem 1rem",
                        borderRadius: "0.65rem",
                        border: "1px solid #bfdbfe",
                        backgroundColor: "#eff6ff",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem"
                      }}
                    >
                      <div style={{ fontWeight: 650, fontSize: "0.9rem", color: "#1e3a8a" }}>
                        {serverProcessing ? "Processing on server…" : "Uploading…"}
                      </div>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#475569", lineHeight: 1.45 }}>
                        Large batches can take several minutes while rows are validated and sent to Workvivo. Do not
                        close this window until it finishes.
                      </p>
                      {serverProcessing ? (
                        <div
                          aria-hidden
                          style={{
                            height: "6px",
                            borderRadius: "999px",
                            background: "linear-gradient(90deg, #93c5fd 0%, #6366f1 50%, #93c5fd 100%)",
                            backgroundSize: "200% 100%",
                            animation: "uploadCsvIndeterminate 1.2s ease infinite"
                          }}
                        />
                      ) : (
                        <div
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={uploadPercent ?? undefined}
                          aria-label="Upload progress"
                          style={{
                            height: "8px",
                            borderRadius: "999px",
                            backgroundColor: "#dbeafe",
                            overflow: "hidden"
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: uploadPercent != null ? `${uploadPercent}%` : "35%",
                              borderRadius: "999px",
                              background: "linear-gradient(90deg, #2563eb, #4f46e5)",
                              transition: "width 0.15s ease-out"
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={uploadMutation.isPending}
                      style={{
                        padding: "0.65rem 1rem",
                        borderRadius: "0.75rem",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        fontWeight: 600,
                        cursor: uploadMutation.isPending ? "not-allowed" : "pointer"
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={uploadMutation.isPending}
                      style={{
                        padding: "0.65rem 1rem",
                        borderRadius: "0.75rem",
                        border: "none",
                        background: uploadMutation.isPending
                          ? "linear-gradient(135deg, #93c5fd, #a5b4fc)"
                          : "linear-gradient(135deg, #2563eb, #4f46e5)",
                        color: "#fff",
                        fontWeight: 600,
                        cursor: uploadMutation.isPending ? "not-allowed" : "pointer"
                      }}
                    >
                      {uploadMutation.isPending ? "Creating job..." : "Create job"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default UploadCsvButton;
